<script setup lang="ts">
import type { Asset } from "@artech-global/types";

type FileItemAction = {
  label: string;
  onClick: (item: FileItem) => void;
};

type FileItem = {
  id: string;
  name: string;
  size: string;
  type: string;
  url?: string;
  progress?: number;
  state: "pending" | "uploading" | "generating" | "uploaded" | "error";
  errorMessage?: string;
  disabled?: boolean;
  removable?: boolean;
  actions?: FileItemAction[];
};

type FileListType = "none" | "single" | "multiple" | "gallery";
type UploadType = "drag-drop" | "button";

type InternalFileItem = FileItem & {
  assetId?: string;
  originalFile?: File;
};

const props = withDefaults(
  defineProps<{
    modelValue?: string | string[];
    label?: string;
    helpText?: string;
    error?: string;
    type?: UploadType;
    fileList?: FileListType;
    accept?: string;
    multiple?: boolean;
    maxFiles?: number;
    maxFileSize?: number;
    disabled?: boolean;
    dragText?: string;
    dragCaption?: string;
    onUpload?: (file: File) => Promise<Asset>;
    onLoad?: (ids: string[]) => Promise<Asset[]>;
  }>(),
  {
    modelValue: undefined,
    label: "",
    helpText: "",
    type: "drag-drop",
    fileList: "single",
    accept: "image/*",
    multiple: false,
    maxFiles: 10,
    maxFileSize: 2 * 1024 * 1024,
    disabled: false,
    dragText: undefined,
    dragCaption: undefined,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string | string[] | undefined): void;
  (e: "uploaded", asset: Asset): void;
  (e: "removed", assetId: string): void;
  (e: "error", error: { file: File; message: string }): void;
}>();

const { $api } = useNuxtApp();
const { t } = useI18n();
const fileUploadRef = ref<{ openFilePicker: () => void }>();

async function defaultLoad(ids: string[]): Promise<Asset[]> {
  const resp = await $api<any>("/assets", { params: { ids: ids.join(","), pageSize: ids.length } });
  return resp.items || [];
}

const internalFiles = ref<InternalFileItem[]>([]);
const isMultiple = computed(() => props.multiple || props.fileList === "multiple" || props.fileList === "gallery");
const effectiveMaxFileSize = computed(() => {
  const accept = props.accept || "";
  if (accept.startsWith("video/") || accept === "*/*") return Math.max(props.maxFileSize, 50 * 1024 * 1024);
  return props.maxFileSize;
});
const pendingUploads = ref<Set<string>>(new Set());

const files = computed<FileItem[]>(() => {
  return internalFiles.value.map((file) => {
    const actions: FileItemAction[] = [];

    if (file.state === "uploaded" && props.fileList === "single") {
      actions.push({
        label: t("ui.fileUpload.replace"),
        onClick: () => handleReplace(),
      });
    }

    if (file.state === "error" && file.originalFile) {
      actions.push({
        label: t("ui.fileUpload.retry"),
        onClick: () => handleRetry(file),
      });
    }

    return {
      ...file,
      actions,
      disabled: file.state === "uploading" || file.disabled,
    };
  });
});

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function generateFileId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function getAssetType(mimeType: string): string {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  return "file";
}

function assetToFileItem(asset: Asset): InternalFileItem {
  return {
    id: `asset-${asset.id}`,
    name: asset.name || asset.originalName || "File",
    size: formatFileSize(asset.size),
    url: asset.url,
    type: asset.type,
    // AI assets stay `generating` until the worker finishes — FileItem shows
    // its generating state (label + spinner) instead of size/actions.
    state: asset.status === "generating" ? "generating" : "uploaded",
    assetId: asset.id,
  };
}

async function uploadFile(file: File): Promise<Asset> {
  const asset = await $api<Asset>("/assets", {
    method: "POST",
    body: {
      name: file.name,
      originalName: file.name,
      type: getAssetType(file.type),
      mimeType: file.type,
      size: file.size,
      accessLevel: "private",
    },
  });

  if (asset.status === "ready") {
    return asset;
  }

  const presignedResponse = await $api<{
    uploadURL: string;
    uploadHeaders: Record<string, string>;
  }>(`/assets/${asset.id}/presigned-url`);

  await fetch(presignedResponse.uploadURL, {
    method: "PUT",
    body: file,
    headers: {
      ...presignedResponse.uploadHeaders,
      "Content-Type": file.type,
    },
  });

  return await $api<Asset>(`/assets/${asset.id}/confirm`, { method: "PUT" });
}

async function handleSelect(selectedFiles: File[]) {
  for (const file of selectedFiles) {
    const fileId = generateFileId();
    const localUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined;
    const fileItem: InternalFileItem = {
      id: fileId,
      name: file.name,
      size: formatFileSize(file.size),
      type: getAssetType(file.type),
      url: localUrl,
      state: "uploading",
      originalFile: file,
    };

    pendingUploads.value.add(fileId);

    if (!isMultiple.value) internalFiles.value = [fileItem];
    else internalFiles.value.push(fileItem);

    try {
      const asset = await (props.onUpload ?? uploadFile)(file);
      if (localUrl) URL.revokeObjectURL(localUrl);
      const idx = internalFiles.value.findIndex((f) => f.id === fileId);
      if (idx !== -1) {
        internalFiles.value[idx] = {
          ...internalFiles.value[idx]!,
          state: "uploaded",
          url: asset.url,
          assetId: asset.id,
        };
      }
      pendingUploads.value.delete(fileId);
      emit("uploaded", asset);
      updateModelValue();
    } catch (error) {
      if (localUrl) URL.revokeObjectURL(localUrl);
      const idx = internalFiles.value.findIndex((f) => f.id === fileId);
      if (idx !== -1) {
        internalFiles.value[idx] = {
          ...internalFiles.value[idx]!,
          state: "error",
          errorMessage: t("message.uploadFailed"),
        };
      }
      pendingUploads.value.delete(fileId);
      emit("error", { file, message: String(error) });
    }
  }
}

function updateModelValue() {
  // Server-backed items (assetId set) stay in the value even while their
  // asset is still generating; local uploads only count once uploaded.
  const assetIds = internalFiles.value
    .filter((f) => f.assetId)
    .map((f) => f.assetId!);
  emit("update:modelValue", isMultiple.value ? (assetIds.length ? assetIds : []) : assetIds[0] || "");
}

function handleReorder(reordered: FileItem[]) {
  internalFiles.value = reordered.map(f =>
    internalFiles.value.find(i => i.id === f.id) ?? (f as InternalFileItem)
  );
  updateModelValue();
}

function handleRemove(file: FileItem) {
  const idx = internalFiles.value.findIndex((f) => f.id === file.id);
  if (idx !== -1) {
    const internalFile = internalFiles.value[idx];
    if (internalFile?.assetId) emit("removed", internalFile.assetId);
    internalFiles.value.splice(idx, 1);
  }
  updateModelValue();
}

function handleReplace() {
  fileUploadRef.value?.openFilePicker();
}

function handleRetry(file: InternalFileItem) {
  if (!file.originalFile) return;
  const idx = internalFiles.value.findIndex((f) => f.id === file.id);
  if (idx !== -1) internalFiles.value.splice(idx, 1);
  handleSelect([file.originalFile]);
}

function getErrorMessage(errors: string[]): string {
  if (errors.includes("FILE_TOO_LARGE")) return t("message.fileTooLarge");
  if (errors.includes("FILE_INVALID_TYPE")) return t("message.invalidFileType");
  if (errors.includes("TOO_MANY_FILES")) return t("message.tooManyFiles");
  return errors.join(", ");
}

function addRejectedFile(file: File, errorMessage: string) {
  const fileItem: InternalFileItem = {
    id: generateFileId(),
    name: file.name,
    size: formatFileSize(file.size),
    type: getAssetType(file.type),
    state: "error",
    errorMessage,
    originalFile: file,
  };

  if (!isMultiple.value) internalFiles.value = [fileItem];
  else internalFiles.value.push(fileItem);
}

function handleReject(rejected: Array<{ file: File; errors: string[] }>) {
  for (const r of rejected) {
    addRejectedFile(r.file, getErrorMessage(r.errors));
    emit("error", { file: r.file, message: r.errors.join(", ") });
  }
}

watch(
  () => props.modelValue,
  async (newValue) => {
    if (pendingUploads.value.size > 0) return;

    if (!newValue) {
      internalFiles.value = [];
      return;
    }

    const ids = Array.isArray(newValue) ? newValue : [newValue];
    const existingIds = internalFiles.value.filter((f) => f.assetId).map((f) => f.assetId);

    if (JSON.stringify(ids.sort()) === JSON.stringify(existingIds.sort())) return;

    const assets = await (props.onLoad ?? defaultLoad)(ids);
    const loadedFiles: InternalFileItem[] = [];
    for (const id of ids) {
      const asset = assets.find((a) => a.id === id);
      if (asset) loadedFiles.push(assetToFileItem(asset));
    }
    internalFiles.value = loadedFiles;
  },
  { immediate: true }
);
</script>

<template>
  <FileUpload
    ref="fileUploadRef"
    :files="files"
    :label="label"
    :error="error"
    :help-text="helpText"
    :type="type"
    :file-list="fileList"
    :accept="accept"
    :multiple="multiple"
    :max-files="maxFiles"
    :max-file-size="effectiveMaxFileSize"
    :disabled="disabled"
    :drag-text="dragText ?? t('ui.fileUpload.dragText')"
    :drag-caption="dragCaption ?? t('ui.fileUpload.dragCaption')"
    :browse-button-text="t('ui.fileUpload.chooseFile')"
    :uploading-text="t('ui.fileUpload.uploading')"
    :generating-text="t('ui.fileUpload.generating')"
    @select="handleSelect"
    @remove="handleRemove"
    @reorder="handleReorder"
    @reject="handleReject"
  >
    <template #trigger="{ api, disabled }" v-if="$slots.trigger">
      <slot name="trigger" :api="api" :disabled="disabled"></slot>
    </template>
  </FileUpload>
</template>
